// This module exports an enum of unique identifiers for elements that need to be located for automated test, and related
// utility functions.

export const TEST_PROPERTY = 'data-test-id';

// create a multipart test ID
export const createTestId = (...args: Array<unknown>): string => args.join('-');

// create test ID selector
export const createTestIdSelector = (...args: Array<unknown>): string => `[${TEST_PROPERTY}="${createTestId(...args)}"]`;

// create test ID selector that matches 'some-string' exactly, or 'some-string-' at beginning (see https://developer.mozilla.org/en-US/docs/Web/CSS/Attribute_selectors)
export const createTestIdPrefixSelector = (...args: Array<unknown>): string => `[${TEST_PROPERTY}|="${createTestId(...args)}"]`;

/**
 * Element identifiers. The naming convention for the key is to specify the page/context first, followed by a brief
 * description of the element. The identifiers are used as the values of the 'data-test-id' attribute for actionable
 * elements in the app. The keys (left-side values) are used by the app code to access and generate identifiers, and
 * by QA to retrieve particular elements.
 *
 * Some test IDs are used as a base for forming  * more specific identifiers. An index may be added. For example,
 * 'search-sidebar-facet-7' could be used to retrieve the 8th facet in a list.
 *
 * A few of the constants defined here are used for something other than data-test-id, eg as an element ID or class.
 */
enum ELEMENT {

  // ***** Elements *****

  rootElementId = '#root',

  // ***** Common *****

  commonErrorPlaceholder      = 'common-error-placeholder',
  commonLoadingPlaceholder    = 'common-loading-placeholder',
  commonTabbedPageHeaderTitle = 'common-tabbed-page-header-title',
  commonPageAlert             = 'common-page-alert',
  resultsPendingClass         = '.dimming-overlay-effect',

  // ***** Nav bar *****

  backButton    = 'back-button',
  helpButton    = 'help-button',
  navScalyrLogo = 'nav-scalyr-logo',

  navLogsButton = 'nav-logs-button',

  navSearchButton             = 'nav-search-button',
  navSearchMenu               = 'nav-search-button-menu',
  navSearchMenuItem           = 'nav-search-button-menu-item',
  navSearchMenuItemIcons      = 'nav-search-button-menu-item-icons',
  navSearchMenuDivider        = 'nav-search-button-menu-divider',
  navSearchNewSearch          = 'nav-search-new-search',
  navSearchTraces             = 'nav-search-traces',
  navSearchPowerQueries       = 'nav-search-power-queries',
  navSearchSavedSearches      = 'nav-search-saved-searches',
  navSearchSavedSearch        = 'nav-search-saved-search',
  navSearchSharedSearches     = 'nav-search-shared-searches',
  navSearchSharedSearch       = 'nav-search-shared-search',
  navSearchSavedSearchEdit    = 'nav-search-saved-search-edit',
  navSearchSavedSearchDelete  = 'nav-search-saved-search-delete',
  navSearchSharedSearchEdit   = 'nav-search-shared-search-edit',
  navSearchSharedSearchDelete = 'nav-search-shared-search-delete',

  navDashboardsButton        = 'nav-dashboards-button',
  navDashboardsMenu          = 'nav-dashboards-button-menu',
  navDashboardsNewDashboard  = 'nav-dashboards-new-dashboard',
  navDashboardsAllDashboards = 'nav-dashboards-all-dashboards',
  navDashboardsDashboard     = 'nav-dashboards-dashboard',

  navAlertsButton = 'nav-alerts-button',

  navStarCustomRules   = 'nav-star-castom-rules',
  navDocsButton        = 'nav-docs-button',
  navDocsMenu          = 'nav-docs-button-menu',
  navDocsSupport       = 'nav-docs-support',
  navDocsWelcome       = 'nav-docs-welcome',
  navDocsStart         = 'nav-docs-start',
  navDocsAgent         = 'nav-docs-agent',
  navDocsIntegrations  = 'nav-docs-integrations',
  navDocsParsers       = 'nav-docs-parsers',
  navDocsSearch        = 'nav-docssearch',
  navDocsPowerQueries  = 'nav-docs-power-queries',
  navDocsGraphs        = 'nav-docs-graphs',
  navDocsDashboards    = 'nav-docs-dashboards',
  navDocsMonitors      = 'nav-docs-monitors',
  navDocsAlerts        = 'nav-docs-alerts',
  navDocsConfiguration = 'nav-docs-configuration',
  navDocsApi           = 'nav-docs-api',
  navDocsReleaseNotes  = 'nav-docs-release-notes',

  navUserButton         = 'nav-user-button',
  navUserMenu           = 'nav-user-button-menu',
  navUserTeam           = 'nav-user-team',
  navUserUser           = 'nav-user-user',
  navUserActiveTeam     = 'nav-user-active-team',
  navUserSwitchTeam     = 'nav-user-switch-team',
  navUserMoreTeams      = 'nav-user-more-teams',
  navUserManageTeams    = 'nav-user-manage-teams',
  navUserManageGroups   = 'nav-user-manage-groups',
  navUserManageUsers    = 'nav-user-manage-users',
  navUserSwitchApp      = 'nav-user-switch-app',
  navUserSwitchTheme    = 'nav-user-switch-theme',
  navUserLabs           = 'nav-user-labs',
  navUserConfigFiles    = 'nav-user-config-files',
  navUserScrubbing      = 'nav-user-scrubbing',
  navUserSecrets        = 'nav-user-secrets',
  navUserParsers        = 'nav-user-parsers',
  navUserMonitors       = 'nav-user-monitors',
  navUserApiKeys        = 'nav-user-api-keys',
  navUserExport         = 'nav-user-export',
  navUserBilling        = 'nav-user-billing',
  navUserCreditCard     = 'nav-user-credit-card',
  navUserPreferences    = 'nav-user-preferences',
  navUserSystemStatus   = 'nav-user-system-status',
  navUserChangePassword = 'nav-user-change-password',
  navUserLogOut         = 'nav-user-log-out',
  navUserBuildInfo      = 'nav-user-build-info',
  navUserUpdateTeamName = 'nav-user-update-team-name',

  navAccountBadge = 'nav-account-badge',

  // ***** Dialogs *****

  // Applies to all confirmation dialogs
  confirmDialog            = 'confirm-dialog',
  confirmDialogTitle       = 'confirm-dialog-title',
  confirmDialogOk          = 'confirm-dialog-ok',
  confirmDialogCancel      = 'confirm-dialog-cancel',

  // "See more" dialog for displaying large values
  seeMoreDialog = 'see-more-dialog',

  // new dashboard dialog
  newDashboardDialog       = 'new-dashboard-dialog',
  newDashboardDialogTitle  = 'new-dashboard-dialog-title',
  newDashboardDialogClose  = 'new-dashboard-dialog-close',
  newDashboardDialogInput  = 'new-dashboard-dialog-input',
  newDashboardDialogOk     = 'new-dashboard-dialog-ok',
  newDashboardDialogCancel = 'new-dashboard-dialog-cancel',

  // rename dashboard dialog
  renameDashboardDialogInput = 'rename-dashboard-dialog-input',
  renameDashboardDialogOk    = 'rename-dashboard-dialog-ok',

  // reset layout dialog
  setLayoutDialog        = 'set-layout-dashboard-dialog',
  setLayoutDialogTitle   = 'set-layout-dashboard-dialog-title',
  setLayoutDialogClose   = 'set-layout-dashboard-dialog-close',
  setLayoutDialogColumns = 'set-layout-dashboard-dialog-input',
  setLayoutDialogFixed   = 'set-layout-dashboard-dialog-input',
  setLayoutDialogOk      = 'set-layout-dashboard-dialog-ok',
  setLayoutDialogCancel  = 'set-layout-dashboard-dialog-cancel',

  // confirm saved search delete
  deleteSearchDialog       = 'delete-search-dialog',
  deleteSearchDialogTitle  = 'delete-search-dialog-title',
  deleteSearchDialogClose  = 'delete-search-dialog-close',
  deleteSearchDialogOk     = 'delete-search-dialog-ok',
  deleteSearchDialogCancel = 'delete-search-dialog-cancel',

  // save search dialog
  saveSearchDialog        = 'save-search-dialog',
  saveSearchDialogTitle   = 'save-search-dialog-title',
  saveSearchDialogClose   = 'save-search-dialog-close',
  saveSearchDialogInput   = 'save-search-dialog-input',
  saveSearchDialogError   = 'save-search-dialog-error',
  saveSearchDialogUrl     = 'save-search-dialog-url',
  saveSearchDialogPrivate = 'save-search-dialog-private',
  saveSearchDialogShared  = 'save-search-dialog-shared',
  saveSearchDialogSave    = 'save-search-dialog-save',
  saveSearchDialogCancel  = 'save-search-dialog-cancel',

  // save alert dialog
  saveAlertDialog                      = 'save-alert-dialog',
  saveAlertDialogTitle                 = 'save-alert-dialog-title',
  saveAlertDialogClose                 = 'save-alert-dialog-close',
  saveAlertDialogDescription           = 'save-alert-dialog-description',
  saveAlertDialogEmail                 = 'save-alert-dialog-email',
  saveAlertDialogFilter                = 'save-alert-dialog-filter',
  saveAlertDialogTrigger               = 'save-alert-dialog-trigger',
  saveAlertDialogTriggerItem           = 'save-alert-dialog-trigger-item', // item index is appended
  saveAlertDialogTriggerValue          = 'save-alert-dialog-trigger-value',
  saveAlertDialogTriggerDuration       = 'save-alert-dialog-trigger-duration',
  saveAlertDialogGracePeriodOn         = 'save-alert-dialog-grace-period-on',
  saveAlertDialogGracePeriod           = 'save-alert-dialog-grace-period',
  saveAlertDialogReminderOn            = 'save-alert-dialog-reminder-on',
  saveAlertDialogReminderPeriod        = 'save-alert-dialog-reminder-period',
  saveAlertDialogNotifyResolvedNow     = 'save-alert-dialog-notify-resolved-now',
  saveAlertDialogNotifyResolvedNever   = 'save-alert-dialog-notify-resolved-never',
  saveAlertDialogNotifyResolvedPeriod  = 'save-alert-dialog-notify-resolved',
  saveAlertDialogNotifyResolvedMinutes = 'save-alert-dialog-notify-resolved-minutes',
  saveAlertDialogSave                  = 'save-alert-dialog-save',
  saveAlertDialogSaveAsNew             = 'save-alert-dialog-save-as-new',
  saveAlertDialogCancel                = 'save-alert-dialog-cancel',

  // save to dashboard dialog
  saveToDashboardDialog                                   = 'save-to-dashboard-dialog',
  saveToDashboardDialogTitle                              = 'save-to-dashboard-dialog-title',
  saveToDashboardDialogClose                              = 'save-to-dashboard-dialog-close',
  saveToDashboardDialogGraphTitle                         = 'save-to-dashboard-dialog-graph-title',
  saveToDashboardDialogExistingDashboard                  = 'save-to-dashboard-dialog-existing-dashboard',
  saveToDashboardDialogExistingDashboardDropdownIndicator = 'save-to-dashboard-dialog-existing-dashboard-dropdown-indicator',
  saveToDashboardDialogExistingDashboardName              = 'save-to-dashboard-dialog-existing-dashboard-name',
  saveToDashboardDialogNewDashboard                       = 'save-to-dashboard-dialog-new-dashboard',
  saveToDashboardDialogNewDashboardName                   = 'save-to-dashboard-dialog-new-dashboard-name',
  saveToDashboardDialogOk                                 = 'save-to-dashboard-dialog-ok',
  saveToDashboardDialogCancel                             = 'save-to-dashboard-dialog-cancel',

  // display settings dialog (except for the account select at the bottom)
  settingsDialog                      = 'settings-dialog',
  settingsDialogTitle                 = 'settings-dialog-title',
  settingsDialogClose                 = 'settings-dialog-close',
  settingsDialogMode                  = 'settings-dialog-mode',
  settingsDialogModeLines             = 'settings-dialog-mode-lines',
  settingsDialogModeWrappedLines      = 'settings-dialog-mode-wrappedLines',
  settingsDialogModeTable             = 'settings-dialog-mode-table',
  settingsDialogSystemField           = 'settings-dialog-system-field',
  settingsDialogSystemFieldDate       = 'settings-dialog-system-field-date',
  settingsDialogSystemFieldTime       = 'settings-dialog-system-field-timestamp',
  settingsDialogSystemFieldSource     = 'settings-dialog-system-field-host',
  settingsDialogSystemFieldLogfile    = 'settings-dialog-system-field-logfile',
  settingsDialogSystemFieldMessage    = 'settings-dialog-system-field-rawLogLine',
  settingsDialogUseAsDefault          = 'settings-dialog-use-as-default',
  settingsDialogChooser               = 'settings-dialog-chooser',
  settingsDialogChooserSource         = 'settings-dialog-chooser-source',
  settingsDialogChooserSourceItem     = 'settings-dialog-chooser-source-item', // item index is appended
  settingsDialogChooserAdd            = 'settings-dialog-chooser-add',
  settingsDialogChooserAddAll         = 'settings-dialog-chooser-add-all',
  settingsDialogChooserMoveUp         = 'settings-dialog-chooser-move-up',
  settingsDialogChooserMoveDown       = 'settings-dialog-chooser-move-down',
  settingsDialogChooserRemove         = 'settings-dialog-chooser-remove',
  settingsDialogChooserTarget         = 'settings-dialog-chooser-target',
  settingsDialogChooserTargetItem     = 'settings-dialog-chooser-target-item', // item index is appended
  settingsDialogChooserTargetAdd      = 'settings-dialog-chooser-target-add',
  settingsDialogChooserTargetAddAll   = 'settings-dialog-chooser-target-add-all',
  settingsDialogChooserTargetMoveUp   = 'settings-dialog-chooser-target-move-up',
  settingsDialogChooserTargetMoveDown = 'settings-dialog-chooser-target-move-down',
  settingsDialogChooserTargetRemove   = 'settings-dialog-chooser-target-remove',
  settingsDialogOk                    = 'settings-dialog-ok',
  settingsDialogCancel                = 'settings-dialog-cancel',
  settingsDialogRestore               = 'settings-dialog-restore',

  // User preferences dialog
  userPreferencesDialog                            = 'user-preferences-dialog',
  userPreferencesDialogThemeSelectRadioLightOption = 'user-preferences-dialog-theme-select-radio-light-option',
  userPreferencesDialogThemeSelectRadioDarkOption  = 'user-preferences-dialog-theme-select-radio-dark-option',
  userPreferencesDialogTimezoneSelect              = 'user-preferences-dialog-timezone-select',
  userPreferencesDialogTimezoneSelectInput         = 'user-preferences-dialog-timezone-select-input',

  // Update team name dialog
  updateTeamNameDialog      = 'update-team-name-dialog',

  // Add annotation dialog
  addAnnotationDialog       = 'add-annotation-dialog',
  addAnnotationDialogInput  = 'add-annotation-dialog-input',
  addAnnotationDialogColor  = 'add-annotation-dialog-color', // button ID; also color with index appended
  addAnnotationDialogOk     = 'add-annotation-dialog-ok',
  addAnnotationDialogCancel = 'add-annotation-dialog-cancel',

  // Post To Slack dialog
  postToSlackDialog       = 'post-to-slack-dialog',
  postToSlackDialogInput  = 'post-to-slack-dialog-input',
  postToSlackDialogOk     = 'post-to-slack-dialog-ok',
  postToSlackDialogCancel = 'post-to-slack-dialog-cancel',

  // ***** Auth pages *****
  authPage                = 'auth-page',

  // ***** Search pages (logs, graph, distribution) *****

  // search toolbar
  searchFilterInput      = 'search-filter-input',
  searchFilterInputClear = 'search-filter-input-clear',
  searchButton           = 'search-button',

  // time range menu
  searchTimeRange         = 'search-time-range',
  searchTimeRangeInterval = 'search-time-range-interval', // number of minutes is appended
  searchTimeRangeStart    = 'search-time-range-start',
  searchTimeRangeEnd      = 'search-time-range-end',
  searchTimeRangeGo       = 'search-time-range-go',
  searchTimeRangeHelp     = 'search-time-range-help',

  // search helper
  searchHelper     = 'search-helper',
  searchHelperHelp = 'search-helper-help',

  // search help panel
  searchHelpClose          = 'search-help-close',
  searchHelpCommand        = 'search-help',
  searchHelpCommandFilter  = 'search-help-command-filter',
  searchHelpCommandLet     = 'search-help-command-let',
  searchHelpCommandParse   = 'search-help-command-parse',
  searchHelpCommandLookup  = 'search-help-command-lookup',
  searchHelpCommandGroup   = 'search-help-command-group',
  searchHelpCommandSort    = 'search-help-command-sort',
  searchHelpCommandLimit   = 'search-help-command-limit',
  searchHelpCommandColumns = 'search-help-command-columns',
  searchHelpCommandJoin    = 'search-help-command-join',
  searchHelpCommandUnion   = 'search-help-command-union',
  searchHelpCommandHelp    = 'search-help-command-help',
  searchHelpCommandInsert  = 'search-help-command-insert',

  // sidebar
  searchSidebarToggle            = 'search-sidebar-toggle',
  searchSidebarSource            = 'search-sidebar-source',
  searchSidebarSourceInput       = 'search-sidebar-source-input',
  searchSidebarSourceInputClose  = 'search-sidebar-source-input-close',
  searchSidebarSourceItem        = 'search-sidebar-source-item', // item index is appended
  searchSidebarLogfile           = 'search-sidebar-logfile',
  searchSidebarLogfileInput      = 'search-sidebar-logfile-input',
  searchSidebarLogfileInputClose = 'search-sidebar-logfile-input-close',
  searchSidebarLogfileItem       = 'search-sidebar-logfile-item', // item index is appended
  searchSidebarFieldsMenu        = 'search-sidebar-fields-menu',
  searchSidebarFacetList         = 'search-sidebar-facet-list',
  searchSidebarFacet             = 'search-sidebar-facet', // facet index is appended
  searchSidebarFacetName         = 'search-sidebar-facet-name', // facet index is appended
  searchSidebarFacetFilter       = 'search-sidebar-facet-filter',
  searchSidebarFacetFilterClose  = 'search-sidebar-facet-filter-close',

  // facet details flyout
  //
  // facet filter test IDs will be based on index of value and the operator, for example:
  //    'facet-details-value-5-less'
  //    'facet-details-value-5-equal'
  //    'facet-details-value-5-not-equal'
  //    'facet-details-value-5-greater'
  facetDetailsClose          = 'facet-details-close',
  facetDetailsGraphValues    = 'facet-details-graph-values',
  facetDetailsGraphMatches   = 'facet-details-graph-matches',
  facetDetailsDistribution   = 'facet-details-distribution',
  facetDetailsGraphMatchesBy = 'facet-details-graph-matches-by',
  facetDetailsSearch         = 'facet-details-search',
  facetDetailsCheckForMore   = 'facet-details-check-for-more',
  facetDetailsSelectAll      = 'facet-details-select-all',

  // facet value and its filter < = != >
  // index appended to each of these; facet values appear in FacetDetails and InspectFieldsDrawer
  facetValue         = 'facet-value',
  facetValueLess     = 'facet-value-less',
  facetValueGreater  = 'facet-value-greater',
  facetValueEqual    = 'facet-value-equal',
  facetValueNotEqual = 'facet-value-not-equal',

  // inspect fields drawer
  inspectFieldsDrawerClose             = 'inspect-fields-drawer-close',
  inspectFieldsDrawerParser            = 'inspect-fields-drawer-parser',
  inspectFieldsDrawerOriginalLog       = 'inspect-fields-drawer-original-log',
  inspectFieldsDrawerThreadLog         = 'inspect-fields-drawer-thread-log',
  inspectFieldsDrawerPodLog            = 'inspect-fields-pod-log',
  inspectFieldsDrawerField             = 'inspect-fields-drawer-field', // field index is appended
  inspectFieldsDrawerServerField       = 'inspect-fields-drawer-server-field', // field index is appended
  inspectFieldsDrawerServerFieldsTitle = 'inspect-fields-drawer-server-fields-title',
  inspectFieldsDrawerCopyButton        = 'inspect-fields-drawer-copy-button',

  // timeline legend
  timelineLegend           = 'timeline-legend',
  timelineLegendTitle      = 'timeline-legend-title',
  timelineLegendSubtitle   = 'timeline-legend-subtitle',
  timelineLegendShowLogs   = 'timeline-legend-show-logs',
  timelineLegendShowGraph  = 'timeline-legend-show-graph',
  timelineLegendTimeRange  = 'timeline-legend-time-range',
  timelineLegendTimeZone   = 'timeline-legend-time-zone',
  timelineLegendNumEvents  = 'timeline-legend-num-events',

  // ***** Search Logs page *****

  // results toolbar
  searchLogsResultsStart    = 'search-logs-results-start',
  searchLogsResultsEnd      = 'search-logs-results-end',
  searchLogsResultsSettings = 'search-logs-results-settings',
  searchLogsResultsHelp     = 'search-logs-results-help',
  searchLogsResultsLiveTail = 'search-logs-results-live-tail',

  // Search Toolbar
  searchToolbar = 'search-toolbar',

  // base for search actions
  searchLogs = 'search-logs',

  // results toolbar: save and share menus, actions
  searchLogsSaveButton          = 'search-logs-save-button',
  searchLogsSaveMenu            = 'search-logs-save-button-menu',
  searchLogsShareButton         = 'search-logs-share-button',
  searchLogsSaveSearch          = 'search-logs-save-search',
  searchLogsSaveSharedSearch    = 'search-logs-save-shared-search',
  searchLogsSaveAsAlert         = 'search-logs-save-as-alert',
  searchLogsSaveToDashboard     = 'search-logs-save-to-dashboard',
  searchLogsDownload            = 'search-logs-download',
  searchLogsDownloadText        = 'search-logs-download-text',
  searchLogsDownloadJSON        = 'search-logs-download-json',
  searchLogsCopyToClipboard     = 'search-logs-copy-to-clipboard',
  searchLogsCopyToClipboardText = 'search-logs-copy-to-clipboard-text',
  searchLogsCopyToClipboardJSON = 'search-logs-copy-to-clipboard-json',
  searchLogsExport              = 'search-logs-export',
  searchLogsCopyLink            = 'search-logs-copy-link',

  // text selection popup
  searchLogsSelectionNew     = 'search-logs-selection-new',
  searchLogsSelectionAdd     = 'search-logs-selection-add',
  searchLogsSelectionExclude = 'search-logs-selection-exclude',

  // logLines components
  searchLogsLogLine           = 'search-logs-log-line',
  searchLogsLogLineDate       = 'search-logs-log-line-date',
  searchLogsLogLineTime       = 'search-logs-log-line-timestamp',
  searchLogsSeverityIcon      = 'search-logs-severity-icon',
  searchLogsTermSelector      = '[data-token-type="term"]',
  searchLogsTermValueSelector = '[data-term-value-type]',

  searchLogsLinesClass   = '.LogLine',
  searchLogsWrappedClass = '.LogLine--Wrapped',
  searchLogsTableClass   = '.BaseTable__row',

  searchLogsCreateRule   = 'create-rule',

  // ***** Search Graph page *****

  // base for search actions
  searchGraph = 'search-graph',

  // graph toolbar: save and share menus, actions
  searchGraphSaveButton       = 'search-graph-save-button',
  searchGraphShareButton      = 'search-graph-share-button',
  searchGraphSaveGraph        = 'search-graph-save-search',
  searchGraphSaveSharedSearch = 'search-graph-save-shared-search',
  searchGraphSaveAsAlert      = 'search-graph-save-as-alert',
  searchGraphSaveToDashboard  = 'search-graph-save-to-dashboard',
  searchGraphDownload         = 'search-graph-download',
  searchGraphExport           = 'search-graph-export',
  searchGraphCopyLink         = 'search-graph-copy-link',
  searchGraphPostToSlack      = 'search-graph-post-to-slack',
  searchGraphHelp             = 'search-graph-help',

  // Compare dropdown
  searchGraphCompareButton   = 'search-graph-compare-button',
  searchGraphComparePrevHour = 'search-graph-compare-prev-hour',
  searchGraphComparePrev4h   = 'search-graph-compare-prev-4h',
  searchGraphComparePrevDay  = 'search-graph-compare-prev-day',
  searchGraphComparePrevWeek = 'search-graph-compare-prev-week',
  searchGraphComparePrev2w   = 'search-graph-compare-prev-2w',
  searchGraphCompareNone     = 'search-graph-compare-none',

  // Breakdown dropdown
  searchGraphBreakdown      = 'search-graph-breakdown',
  searchGraphBreakdownInput = 'search-graph-breakdown-input',
  searchGraphBreakdownItem  = 'search-graph-breakdown-item',  // index is appended

  // Chart type dropdown
  searchGraphChartTypeButton           = 'search-graph-chart-type-button',
  searchGraphChartTypeSubmit           = 'search-graph-chart-type-submit',
  searchGraphChartTypeArea             = 'search-graph-chart-type-area',
  searchGraphChartTypeLine             = 'search-graph-chart-type-line',
  searchGraphChartTypeStackedBar       = 'search-graph-chart-type-stacked-bar',
  searchGraphChartTypeLinesStraight    = 'search-graph-chart-type-lines-straight',
  searchGraphChartTypeLinesSmooth      = 'search-graph-chart-type-lines-smooth',
  searchGraphChartTypeYAxisLinear      = 'search-graph-chart-type-yaxis-linear',
  searchGraphChartTypeYAxisLogarithmic = 'search-graph-chart-type-yaxis-logarithmic',

  // Legend: Plot groups
  searchGraphPlotGroupFunctions          = 'search-graph-plot-group-functions',
  searchGraphPlotGroupFunctionsMean      = 'search-graph-plot-group-functions-mean',
  searchGraphPlotGroupFunctionsMin       = 'search-graph-plot-group-functions-min',
  searchGraphPlotGroupFunctionsMax       = 'search-graph-plot-group-functions-max',
  searchGraphPlotGroupFunctionsSum       = 'search-graph-plot-group-functions-sum',
  searchGraphPlotGroupFunctionsSumPerSec = 'search-graph-plot-group-functions-sum-per-sec',
  searchGraphPlotGroupPercentiles        = 'search-graph-plot-group-percentiles',
  searchGraphPlotGroupPercentilesP10     = 'search-graph-plot-group-percentiles-10',
  searchGraphPlotGroupPercentilesP50     = 'search-graph-plot-group-percentiles-50',
  searchGraphPlotGroupPercentilesP90     = 'search-graph-plot-group-percentiles-90',
  searchGraphPlotGroupPercentilesP95     = 'search-graph-plot-group-percentiles-95',
  searchGraphPlotGroupPercentilesP99     = 'search-graph-plot-group-percentiles-99',
  searchGraphPlotGroupPercentilesP999    = 'search-graph-plot-group-percentiles-999',

  searchGraphPlotGroupFunctionsMain      = 'search-graph-plot-group-functions-main',
  searchGraphPlotGroupCompareToShifted   = 'search-graph-plot-group-functions-shifted',
  searchGraphPlotGroupDeltaDelta         = 'search-graph-plot-group-functions-delta',


  // Legend: Selection deltas
  searchGraphSelectionDeltas = 'search-graph-selection-deltas',

  // Graph buttons
  searchGraphZoomIn        = 'search-graph-zoom-in',
  searchGraphAddAnnotation = 'search-graph-add-annotation',

  searchGraphsHighchartsPointClass = '.highcharts-point',

  // Graph style select dropdown menu
  graphStyleDropdown      = 'graph-style-dropdown',
  graphStyleDropdownTable = 'graph-style-dropdown-table',
  graphStyleDropdownLine  = 'graph-style-dropdown-line',
  graphStyleDropdownArea  = 'graph-style-dropdown-area',
  graphStyleDropdownBar   = 'graph-style-dropdown-bar',
  graphStyleDropdownPie   = 'graph-style-dropdown-pie',

  // Graph Settings dropdown menu
  graphDisplaySettingsStraight    = 'graph-display-settings-straight',
  graphDisplaySettingsSmooth      = 'graph-display-settings-smooth',
  graphDisplaySettingsLinear      = 'graph-display-settings-linear',
  graphDisplaySettingsLogarithmic = 'graph-display-settings-logarithmic',
  graphDisplaySettingsSubmit      = 'graph-display-settings-submit',

  // ***** Search Distribution page *****

  // base for search actions
  searchDistribution = 'search-distribution',

  // graph toolbar: save and share menus, actions
  searchDistributionSaveButton            = 'search-distribution-save-button',
  searchDistributionShareButton           = 'search-distribution-share-button',
  searchDistributionSaveGraph             = 'search-distribution-save-search',
  searchDistributionSaveSharedSearch      = 'search-distribution-save-shared-search',
  searchDistributionSaveAsAlert           = 'search-distribution-save-as-alert',
  searchDistributionSaveToDashboard       = 'search-distribution-save-to-dashboard',
  searchDistributionDownload              = 'search-distribution-download',
  searchDistributionCopyLink              = 'search-distribution-copy-link',

  // y-axis scale options
  searchDistributionYScaleLinear          = 'search-distribution-y-scale-linear',
  searchDistributionYScaleLogarithmic     = 'search-distribution-y-scale-logarithmic',

  // ***** Power Query page *****

  powerQueryPage = 'power-query-page',

  // input text area and search button
  powerQueryInput        = 'power-query-input',
  powerQuerySearchButton = 'power-query-search-button',

  // commands
  powerQueryCommand       = 'power-query-command',
  powerQueryCommandHelp   = 'power-query-command-help',
  powerQueryCommandInsert = 'power-query-command-insert',

  // base for power query actions
  powerQuery = 'power-query',

  // actions menu
  powerQueryActionsButton    = 'power-query-actions',
  powerQuerySaveSearch       = 'power-query-save-search',
  powerQuerySaveToDashboard  = 'power-query-save-to-dashboard',
  powerQueryDownload         = 'power-query-download',
  powerQueryDownloadCSV      = 'power-query-download-csv',
  powerQueryCopyLink         = 'power-query-copy-link',
  powerQueryCopyToClipboard  = 'power-query-copy-to-clipboard',
  powerQuerySaveSharedSearch = 'power-query-save-shared-search',
  powerQueryHelp             = 'power-query-help',

  // query example
  powerQueryExampleCode    = 'power-query-example-code',
  powerQueryExampleArrow   = 'power-query-example-arrow',
  powerQueryExampleResults = 'power-query-example-results',

  // results table
  powerQueryTableContainer = 'power-query-table-container',
  powerQueryTableRow       = 'power-query-table-row', // index is appended

  // error UI
  powerQueryNetworkErrorPlaceholder = 'power-query-network-error-placeholder',

  // ***** Combined search page *****

  // base for search actions
  searchPage = 'search-page',

  // views
  searchPageEvents    = 'search-page-events',
  searchPageAnalyze   = 'search-page-analyze',
  searchPageVisualize = 'search-page-visualize',

  // actions
  searchPageStart           = 'search-page-start',
  searchPageEnd             = 'search-page-end',
  searchPageOlder           = 'search-page-older',
  searchPageNewer           = 'search-page-newer',
  searchPageSaveButton      = 'search-page-save-button',
  searchPageShareButton     = 'search-page-share-button',
  searchPageLiveTail        = 'search-page-live-tail',
  searchPageDisplaySettings = 'search-page-display-settings',

  // ***** Live Tail Page *****

  liveTailSearchButton           = 'live-tail-search-button',
  liveTailPauseButton            = 'live-tail-pause-button',
  liveTailResumeButton           = 'live-tail-resume-button',
  liveTailHeaderCloseIcon        = 'live-tail-header-close-icon',

  // ***** Billing And Usage page *****

  planPageBillingSettings                  = 'plan-page-billing-settings',
  planPageUsageGraph                       = 'plan-page-usage-graph',
  planPageUsageGraphOverview               = 'plan-page-usage-graph-overview',
  planPageCostManagementTableRow           = 'plan-page-cost-management-table-row',
  planPageCostManagementTableFooter        = 'plan-page-cost-management-table-footer',
  planPageNewCategoryFormNameInput         = 'plan-page-new-category-form-name-input',
  planPageNewCategoryFormPlannedUsageInput = 'plan-page-new-category-form-planned-usage-input',
  planPageNewCategoryFormFilterInput       = 'plan-page-new-category-form-filter-input',
  planPageAddNewCategoryButton             = 'plan-page-add-new-category-button',
  planPageSaveNewCategoryButton            = 'plan-page-save-new-category-button',
  planPageCancelNewCategoryButton          = 'plan-page-cancel-new-category-button',
  planPageLogCategoryTable                 = 'plan-page-log-category-table',
  planPageDeleteCategoryButton             = 'plan-page-delete-category-button',
  planPageEditCategoryButton               = 'plan-page-edit-category-button',
  planPageOpenSearchForCategoryButton      = 'plan-page-open-search-for-category-button',

  // Discard Filters
  planPageLogCategoryDiscardFiltersContainer   = 'plan-page-log-category-discard-filters-container',
  planPageLogCategoryDiscardFilters            = 'plan-page-log-category-discard-filters',
  planPageLogCategoryDiscardFilter             = 'plan-page-log-category-discard-filter',
  planPageCreateDiscardFilter                  = 'plan-page-create-discard-filter',
  planPageDiscardFilterFormFilterInput         = 'plan-page-discard-filter-form-filter-input',
  planPageDiscardFilterFormSaveButton          = 'plan-page-discard-filter-form-save-button',
  planPageDiscardFilterFormCancelButton        = 'plan-page-discard-filter-form-cancel-button',
  planPageDiscardFilterSwitchOnOff             = 'plan-page-switch-category-on-off',
  planPageDiscardFilterDeleteButton            = 'plan-page-delete-category-button',
  planPageDiscardFilterEditButton              = 'plan-page-edit-category-button',
  planPageDiscardFilterOpenSearchButton        = 'plan-page-open-search-for-category-button',
  planPageDiscardFilterDisableFormUntilInput   = 'plan-page-discard-filter-disable-form-until-input',
  planPageDiscardFilterDisableFormSaveButton   = 'plan-page-discard-filter-disable-form-save-button',
  planPageDiscardFilterDisableFormCancelButton = 'plan-page-discard-filter-disable-form-cancel-button',

  // Notifications
  planPageLogOverageNotificationContainer    = 'plan-page-log-overage-notification-container',
  planPageLogCategoryNotificationContainer   = 'plan-page-log-category-notification-container',
  planPageLogOverageNotificationSwitchOnOff  = 'plan-page-log-overage-notification-switch-on-off',
  planPageLogCategoryNotificationSwitchOnOff = 'plan-page-log-category-notification-switch-on-off',
  planPageLogOverageNotificationEmailInput   = 'plan-page-log-overage-notification-email-input',
  planPageLogCategoryNotificationEmailInput  = 'plan-page-log-category-notification-email-input',
  planPageLogOverageNotificationSizeInput    = 'plan-page-log-overage-notification-size-input',
  planPageLogOverageNotificationSaveButton   = 'plan-page-log-overage-notification-save-button',
  planPageLogCategoryNotificationSaveButton  = 'plan-page-log-category-notification-save-button',

  // ***** Overview page *****

  overviewPage                    = 'overview-page',
  overviewLogVolumeGraph          = 'overview-log-volume-graph',
  overviewSourcesHeaderSource     = 'overview-sources-header-source',
  overviewSourcesHeaderLogfile    = 'overview-sources-header-logfile',
  overviewSourcesHeaderDashboards = 'overview-sources-header-dashboards',
  overviewDashboard               = 'overview-dashboard',
  overviewHelp                    = 'overview-help',
  overviewShowAll                 = 'overview-show-all',
  overviewAddMoreHosts            = 'overview-add-more-hosts',
  overviewAddMoreLogs             = 'overview-add-more-logs',
  overviewSourceName              = 'overview-source-name',
  overviewLogfile                 = 'overview-logfile',
  overviewClusterName             = 'overview-cluster-name',
  overviewController              = 'overview-controller',
  overviewAllLogs                 = 'overview-all-logs',
  overviewControllerDashboard     = 'overview-cluster-dashboard',
  overviewControllerLabel         = 'overview-cluster-label',
  overviewFilterInput             = 'overview-filter-input',
  overviewShowLogs                = 'overview-show-logs',
  overviewShowControllers         = 'overview-show-controllers',

  // ***** Dashboard page *****

  // header
  dashboardHeaderAddMenu          = 'dashboard-header-add-menu',
  dashboardHeaderAddMenuGraph     = 'dashboard-header-add-menu-graph',
  dashboardHeaderAddMenuBreakdown = 'dashboard-header-add-menu-breakdown',
  dashboardHeaderAddMenuPie       = 'dashboard-header-add-menu-pie',
  dashboardHeaderAddMenuTable     = 'dashboard-header-add-menu-table',
  dashboardHeaderAddMenuNumber    = 'dashboard-header-add-menu-number',
  dashboardHeaderAddMenuMarkdown  = 'dashboard-header-add-menu-markdown',
  dashboardHeaderAddMenuHoneycomb = 'dashboard-header-add-menu-honeycomb',
  dashboardHeaderAddMenuDonut     = 'dashboard-header-add-menu-donut',
  dashboardHeaderAddTabs          = 'dashboard-header-add-tabs',
  dashboardHeaderMore             = 'dashboard-header-more',
  dashboardHeaderEditJson         = 'dashboard-header-edit-json',
  dashboardHeaderRename           = 'dashboard-header-rename',
  dashboardHeaderDelete           = 'dashboard-header-delete',
  dashboardHeaderFullScreenEnter  = 'dashboard-header-full-screen-enter',
  dashboardHeaderFullScreenExit   = 'dashboard-header-full-screen-exit',
  dashboardHeaderHelp             = 'dashboard-header-help',
  dashboardHeaderReset            = 'dashboard-header-reset',
  dashboardHeaderLock             = 'dashboard-header-lock',
  dashboardHeaderRefresh          = 'dashboard-header-refresh',
  dashboardHeaderRefreshInterval  = 'dashboard-header-refresh-interval', // number is appended
  dashboardHeaderTitle            = 'dashboard-header-title', // number is appended
  dashboardHeaderClose            = 'dashboard-header-close', // exit single graph view
  dashboardHeaderAddEditFilters   = 'dashboard-header-add-edit-filters',

  // empty dashboard
  dashboardEmptySearchLink        = 'dashboard-empty-search-link',

  // dashboard header controls
  dashboardHeaderControlsFacetFilter      = 'dashboard-header-controls-facet-filter',
  dashboardHeaderControlsCustomFilter     = 'dashboard-header-custom-controls-filter',
  dashboardHeaderControlsAddFilterButton  = 'dashboard-header-controls-add-filter-button',
  dashboardHeaderControlsDoneButton       = 'dashboard-header-controls-done-button',
  dashboardHeaderControlsEditButton       = 'dashboard-header-controls-edit-button',
  dashboardHeaderControlsDeleteButton     = 'dashboard-header-controls-delete-button',

  // dashboards list
  dashboardsList                  = 'dashboards-list',
  dashboardsListName              = 'dashboards-list-name',
  dashboardsListChiclet           = 'dashboards-list-chiclet',
  dashboardsListFilter            = 'dashboards-list-filter',

  // add graph dialog
  dashboardAddGraphTitle          = 'dashboard-add-graph-title',
  dashboardAddGraphFilter         = 'dashboard-add-graph-filter',
  dashboardAddGraphInterval       = 'dashboard-add-graph-interval', // number of minutes is appended
  dashboardAddGraphBars           = 'dashboard-add-graph-bars',
  dashboardAddGraphLine           = 'dashboard-add-graph-line',
  dashboardAddGraphStacked        = 'dashboard-add-graph-stacked',
  dashboardAddGraphStackedBar     = 'dashboard-add-graph-stacked-bar',
  dashboardAddGraphStraight       = 'dashboard-add-graph-straight',
  dashboardAddGraphSmooth         = 'dashboard-add-graph-smooth',
  dashboardAddGraphAddPlot        = 'dashboard-add-graph-add-plot',
  dashboardAddGraphBreakdown      = 'dashboard-add-graph-breakdown',
  dashboardAddGraphFacet          = 'dashboard-add-graph-facet',
  dashboardAddGraphBreakdownInput = 'dashboard-add-graph-breakdown-input',
  dashboardAddGraphBreakdownItem  = 'dashboard-add-graph-breakdown-item', // item index is appended
  dashboardAddGraphOk             = 'dashboard-add-graph-ok',
  dashboardAddGraphCancel         = 'dashboard-add-graph-cancel',

  // dashboard add/edit prefilters dialog
  dashboardAddPrefilters                    = 'dashboard-add-prefilters',
  dashboardAddPrefiltersFieldCheckbox       = 'dashboard-add-prefilters-field-checkbox',
  dashboardAddPrefiltersCustomCheckbox      = 'dashboard-add-prefilters-custom-checkbox',
  dashboardAddPrefiltersField               = 'dashboard-add-prefilters-field',
  dashboardAddPrefiltersFieldNameInput      = 'dashboard-add-prefilters-field-name-input',
  dashboardAddPrefiltersDefaultValueInput   = 'dashboard-add-prefilters-default-value-input',
  dashboardAddPrefiltersCustomNameInput     = 'dashboard-add-prefilters-custom-name-input',
  dashboardAddPrefiltersMenuItemLabel       = 'dashboard-add-prefilters-menu-item-label',
  dashboardAddPrefiltersMenuItemValue       = 'dashboard-add-prefilters-menu-item-value',
  dashboardAddPrefiltersCustomAddOption     = 'dashboard-add-prefilters-custom-add-option',
  dashboardAddPrefiltersOkButton            = 'dashboard-add-prefilters-ok',

  // edit graph dialog
  dashboardEditGraph              = 'dashboard-edit-graph',

  // add plot dialog
  dashboardAddPlotBasicTab    = 'dashboard-add-plot-basic-tab',
  dashboardAddPlotJsonTab     = 'dashboard-add-plot-json-tab',
  dashboardAddPlotFunction    = 'dashboard-add-plot-function', // function is appended
  dashboardAddPlotField       = 'dashboard-add-plot-field',
  dashboardAddPlotFieldInput  = 'dashboard-add-plot-field-input',
  dashboardAddPlotFieldItem   = 'dashboard-add-plot-field-item', // item index is appended
  dashboardAddPlotFilter      = 'dashboard-add-plot-filter',
  dashboardAddPlotLabel       = 'dashboard-add-plot-label',
  dashboardAddPlotColor       = 'dashboard-add-plot-color',
  dashboardAddPlotColorToggle = 'dashboard-add-plot-color-toggle',
  dashboardAddPlotOk          = 'dashboard-add-plot-ok',
  dashboardAddPlotCancel      = 'dashboard-add-plot-cancel',

  // dashboard graph
  dashboardGraph              = 'dashboard-graph',
  dashboardGraphTitle         = 'dashboard-graph-title',
  dashboardGraphTitleIcons    = 'dashboard-graph-title-icons',
  dashboardViewInSearchIcon   = 'dashboard-view-in-search-icon',

  // dashboard view graph
  dashboardViewGraph          = 'dashboard-view-graph',

  // ***** Alerts page *****

  alertsPage                            = 'alerts-page',
  alertTriggeredTab                     = 'alert-triggered-tab',
  alertMutedTab                         = 'alert-muted-tab',
  alertIncompleteTab                    = 'alert-incomplete-tab',
  alertAllTab                           = 'alert-all-tab',
  alertFilterInput                      = 'alert-filter-input',
  alertSettingsDropdown                 = 'alert-settings-dropdown',
  alertJSONLink                         = 'alert-json-link',
  alertHelpButton                       = 'alert-help-button',
  alertAddAlertButton                   = 'alert-add-alert-button',
  alertSelectAllCheckbox                = 'alert-select-all-checkbox',
  alertMuteButton                       = 'alert-mute-button',
  alertMute1hourOption                  = 'alert-mute-button-60',
  alertUnmuteButton                     = 'alert-unmute-button',
  alertDeleteButton                     = 'alert-delete-button',
  alertTimestampHeader                  = 'alert-timestamp-header',
  alertMuteDropdown                     = 'alert-mute-dropdown',
  alertListItem                         = 'alert-list-item',
  alertListItemCheckbox                 = 'alert-list-item-checkbox',
  alertSearchIconButton                 = 'alert-search-icon-button',
  alertEditIconButton                   = 'alert-edit-icon-button',
  alertUnmuteIconButton                 = 'alert-unmute-icon-button',
  alertTitleDetailsLink                 = 'alert-title-details-link',

  // ***** Alert Details Page *****

  alertDetailsDescription               = 'alert-details-description',
  alertDetailsTriggerExpression         = 'alert-details-trigger-expression',
  alertDetailsGraceNotification         = 'alert-details-grace-notification',
  alertDetailsReminderNotification      = 'alert-details-reminder-notification',
  alertDetailsResolutionNotification    = 'alert-details-resolution-notification',
  alertDetailsTimeSeriesGraph           = 'alert-details-time-series-graph',
  alertDetailsHelpButton                = 'alert-details-help-button',
  alertDetailsEditButton                = 'alert-details-edit-button',
  alertDetailsDeleteButton              = 'alert-details-delete-button',
  alertDetailsMuteButton                = 'alert-details-mute-button',
  alertDetailsMute1HourOption           = 'alert-details-mute-button-60',
  alertDetailsUnmuteButton              = 'alert-details-unmute-button',
  alertDetailsMuteDate                  = 'alert-details-mute-date',
  alertDetailsSearchLink                = 'alert-details-search-link',
  alertDetailsEmailAddresses            = 'alert-details-email-addresses',

  // ***** Link account page *****

  linkAccountPage                     = 'link-account-page',
  linkAccountPageEmailInput           = 'link-account-page-email-input',
  linkAccountPageEmailInputInvalid    = 'link-account-page-email-input-invalid',
  linkAccountPagePasswordInput        = 'link-account-page-password-input',
  linkAccountPagePasswordInputInvalid = 'link-account-page-password-input-invalid',
  linkAccountPageSetLinkButton        = 'link-account-page-set-link-button',
  linkAccountPageStatus               = 'link-account-page-status',
  linkAccountPageModal                = 'link-account-page-modal',
  linkAccountPageModalBody            = 'link-account-page-modal-body',
  linkAccountPageModalOkButton        = 'link-account-page-modal-ok-button',
  linkAccountPageEmailSwitchItem      = 'link-account-page-email-switch-item',

  // ***** Manage groups page *****

  manageGroupsPage               = 'manageGroupsPage',
  manageGroupsTable              = 'manageGroupsTable',
  manageGroupsAccessSelect       = 'manageGroupsAccessSelect',
  manageGroupsAllowedSearchInput = 'manageGroupsAllowedSearchInput',

  // ***** User accounts page *****

  userAccountsPage                         = 'user-accounts-page',
  userAccountsSendInvitesButton            = 'user-accounts-send-invites-button',
  userAccountsAdvancedConfigurationButton  = 'user-accounts-advanced-configuration-button',
  userAccountsTeamTableEmailCell           = 'user-accounts-team-table-email-cell',
  userAccountsTeamTableRow                 = 'user-accounts-team-table-row',
  userAccountsResendInviteButton           = 'user-accounts-resend-invite-button',
  userAccountsRevokeInviteButton           = 'user-accounts-revoke-invite-button',
  userAccountsRevokeAccessButton           = 'user-accounts-revoke-access-button',
  userAccountsAllowedSearchInput           = 'user-accounts-allowed-search-input',
  userAccountsDashboardsSelect             = 'user-accounts-dashboards-select',
  userAccountsAccessSelect                 = 'user-accounts-access-select',
  userAccountsEmailInput                   = 'user-accounts-email-input',

  // ***** Labs page *****

  labsPage                      = 'labs-page',
  labsPageHeaderEmail           = 'labs-page-header-email',
  labsPageLab                   = 'labs-page-lab',
  labsPageLabDescriptionActions = 'labs-page-lab-description-actions',
  labsPageLabTitle              = 'labs-page-lab-title',

  // ***** Configuration Files page *****

  configurationFilesPage = 'configuration-files-page',

  // ***** Scrubbing page *****

  scrubbingPage = 'scrubbing-page',

  // ***** Parser page *****

  logSampleResults                   = 'log-sample-results',
  logSampleInput                     = 'log-sample-input',
  saveParserButton                   = 'save-parser-button',
  formatParserButton                 = 'format-parser-button',
  logSampleContainer                 = 'log-sample-container',
  logSampleContainerNoContentMessage = 'log-sample-container-no-content-message',
  parserOutputContent                = 'parser-output-content',
  updateSampleButton                 = 'update-sample-button',
  testParserButton                   = 'test-parser-button',
  logSampleHeader                    = 'log-sample-header',
  parserOutputHeader                 = 'parser-output-header',
  parserLogLine                      = 'parser-log-line',

  // ***** Parsers page *****

  parsersPage               = 'parsers-page',
  addParserButton           = 'add-parser-button',
  newParserInput            = 'new-parser-input',
  createParserDialog        = 'create-parser-dialog',
  createParserDialogOk      = 'create-parser-dialog-ok',
  agentInstallationLink     = 'agent-installation-link',
  addNewLogFilesLink        = 'add-new-log-files-link',
  parsersTableRow           = 'parsers-table-row',
  createParserRequestButton = 'create-parser-request-button',
  createParserButton        = 'create-parser-button',
  customParserDialog        = 'custom-parser-dialog',
  customParserDialogOk      = 'custom-parser-dialog-ok',

  // ***** Secrets page *****

  secretsPage               = 'secrets-page',
  secretsTableRow           = 'secrets-table-row',
  createSecretDialog        = 'create-secret-dialog',
  createSecretDialogOk      = 'create-secret-dialog-ok',
  addSecretDialog           = 'add-secret-dialog',
  addSecretDialogOK         = 'add-secret-dialog-ok',
  editSecretDialog          = 'edit-secret-dialog',
  editSecretDialogOK        = 'edit-secret-dialog-ok',
  deleteSecretDialog        = 'delete-secret-dialog',
  deleteSecretDialogOK      = 'delete-secret-dialog-ok',
  addSecretButton           = 'add-secret-button',
  deleteSecretButton        = 'delete-secret-button',
  modifySecretButton        = 'modify-secret-button',
  createSecretButton        = 'create-secret-button',
  secretInput               = 'secret-input',
  newSecretInput            = 'new-secret-input',
  secretEditDialog          = 'secret-edit-dialog',


  // ***** API keys page *****

  apiKeysPage                                 = 'api-keys-page',
  apiKeysLogAccess                            = 'api-keys-log-access',
  apiKeysConfigAccess                         = 'api-keys-config-access',
  apiKeysLogAccessAddKeyButton                = 'api-keys-log-access-add-key-button',
  apiKeysConfigAccessAddKeyButton             = 'api-keys-config-access-add-key-button',
  apiKeysLogAccessAddKeyButtonMenuItem0       = 'api-keys-log-access-add-key-button-menu-item-item-0',
  apiKeysLogAccessAddKeyButtonMenuItem1       = 'api-keys-log-access-add-key-button-menu-item-item-1',
  apiKeysConfigAccessAddKeyButtonMenuItem0    = 'api-keys-config-access-add-key-button-menu-item-item-0',
  apiKeysConfigAccessAddKeyButtonMenuItem1    = 'api-keys-config-access-add-key-button-menu-item-item-1',
  apiKeysLogAccessSectionHeader               = 'api-keys-log-access-section-header',
  apiKeysConfigAccessSectionHeader            = 'api-keys-config-access-section-header',
  apiKeysLogAccessTable                       = 'api-keys-log-access-table',
  apiKeysConfigAccessTable                    = 'api-keys-config-access-table',
  apiKeysLogAccessTableHeader                 = 'api-keys-log-access-table-header',
  apiKeysConfigAccessTableHeader              = 'api-keys-config-access-table-header',
  apiKeysLogAccessTableRow                    = 'api-keys-log-access-table-row',
  apiKeysConfigAccessTableRow                 = 'api-keys-config-access-table-row',
  apiKeysLogAccessDeleteIcon                  = 'api-keys-log-access-delete-icon',
  apiKeysConfigAccessDeleteIcon               = 'api-keys-config-access-delete-icon',
  apiKeysLogAccessEditIcon                    = 'api-keys-log-access-edit-icon',
  apiKeysConfigAccessEditIcon                 = 'api-keys-config-access-edit-icon',
  apiKeysEditDialog                           = 'api-keys-edit-dialog',
  apiKeysCopyToClipboardIcon                  = 'api-keys-copy-to-clipboard-icon',
  // ***** Bulk exports page *****

  bulkExportsPage           = 'bulk-exports-page',
  bulkExportsPageEmailInput = 'bulk-exports-page-email-input',

  // ***** Billing page *****

  billingPage = 'billing-page',

  hindsightStorageTab  = 'hindsight-storage-tab',
  hindsightSearchesTab = 'hindsight-searches-tab',
  hindsightSearchQuery = 'hindsight-search-query',
  hindsightSearchUser  = 'hindsight-search-user',
  hindsightSearchTeams = 'hindsight-search-teams',

  // ***** Change password page *****

  changePasswordPage = 'change-password-page',

  // ***** Change password page (public) *****

  changePasswordPagePublic                                = 'change-password-public-page',
  changePasswordPagePublicChangePasswordForm              = 'change-password-public-page-change-password-form',
  changePasswordPagePublicRequestPasswordResetEmailForm   = 'change-password-public-page-request-password-reset-email-form',
  changePasswordPagePublicEmailInput                      = 'change-password-public-page-email-input',
  changePasswordPagePublicNewPasswordInput                = 'change-password-public-page-new-password-input',
  changePasswordPagePublicConfirmNewPasswordInput         = 'change-password-public-page-confirm-new-password-input',
  changePasswordPagePublicChangePasswordButton            = 'change-password-public-page-change-password-button',
  changePasswordPagePublicRequestResetButton              = 'change-password-public-page-request-reset-button',

  // ***** Page header *****

  pageHeader = 'page-header',
  pageHeaderTitle = 'page-header-title',

  // ***** Credit Card Page *****

  creditCardPageAddCreditCardButton   = 'credit-card-page-add-credit-card-button',
  creditCardPageEditCreditCardButton  = 'credit-card-page-edit-credit-card-button',

  // ***** Monitors page *****
  monitorsPage                            = 'monitors-page',
  monitorsJSONLink                        = 'monitors-json-link',
  monitorsHelpButton                      = 'monitors-help-button',
  monitorsTimeRange                       = 'monitors-time-range',

  // Link
  linkPerformanceMarker = 'link-performance-marker',

  // Configuration Files List
  configurationFilesListItem = 'configuration-files-list-item',

  // Configuration File
  configurationFilePrettyPrintButton = 'configuration-file-pretty-print-button',
  configurationFileSaveButton        = 'configuration-file-save-button',
  configurationFilesListTable        = 'configuration-file-list-table',

  // ***** Restricted Access Error Page *****
  accessDeniedError = 'access-denied-error',

  // ***** Page Not Found (404) *****
  pageNotFound                          = 'page-not-found',
  pageNotFoundPublicBrokenLinkPrompt    = 'page-not-found-public-broken-link-prompt',
  pageNotFoundPrivateBrokenLinkPrompt   = 'page-not-found-private-broken-link-prompt',
  pageNotFoundPrivateReportButton       = 'page-not-found-private-report-button',
  pageNotFoundGoBackButton              = 'page-not-found-go-back-button',

  acceptLinkInvite                      = 'accept-link-invite',
  acceptLinkInviteButtonLink            = 'accept-link-invite-button-link',
  acceptLinkInviteButtonCreate          = 'accept-link-invite-button-create',
  acceptLinkInviteInvitee               = 'accept-link-invite-button-invitee',
  acceptLinkInviteInviter               = 'accept-link-invite-button-inviter',
  acceptLinkInvitePassword              = 'accept-link-invite-button-password',
  acceptLinkInviteTokenError            = 'accept-link-invite-error',
  acceptLinkInviteErrorLogin            = 'accept-link-invite-error-login',
  acceptLinkInviteErrorLogout           = 'accept-link-invite-error-logout',

  // ***** Try Scalyr Page *****

  marketingFormFirstNameInput               = 'marketing-form-first-name-input',
  marketingFormLastNameInput                = 'marketing-form-last-name-input',
  marketingFormEmailInput                   = 'marketing-form-email-input',
  marketingFormCompanyInput                 = 'marketing-form-company-input',
  marketingFormContinueButton               = 'marketing-form-continue-button',
  marketingFormFirstNameInputError          = 'marketing-form-first-name-input-invalid',
  marketingFormLastNameInputError           = 'marketing-form-last-name-input-invalid',
  marketingFormEmailInputError              = 'marketing-form-email-input-invalid',
  marketingFormCompanyInputError            = 'marketing-form-company-input-invalid',
  marketingFormStateInputError              = 'marketing-form-state-input-invalid',
  marketingFormCountryInput                 = 'marketing-form-country-input',
  marketingFormPhoneInput                   = 'marketing-form-phone-input',
  marketingFormPhoneInputError              = 'marketing-form-phone-input-invalid',
  marketingFormComments                     = 'marketing-form-comments-input',

  // ***** Sign up Page *****
  signUpFormPasswordInput                   = 'sign-up-form-password-input',
  signUpFormPasswordInputError              = 'sign-up-form-password-input-invalid',

}

export type ElementKey = keyof typeof ELEMENT;

export type DynamicTestId = ELEMENT | string;

export default ELEMENT;
